import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { API } from 'aws-amplify';
import MultiSelect from 'react-multi-select-component';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { useTranslation } from 'react-i18next';
import { useFeature } from 'flagged';
import useUIState from '../../hooks/useUIstate';
import KPICards from './KPICards';
import RevenueChart from './RevenueChart';
import StoreComparisonChart from './StoreComparisonChart';
import YearComparisonChart from './YearComparisonChart';
import ItemSalesChart from './ItemSalesChart';
import ProductsDashboard from './Products/ProductsDashboard';
import OffersDashboard from './Offers/OffersDashboard';
import ExportOverviewButton from './ExportOverviewButton';
import { useProductsAndOffers } from './hooks/useProductsAndOffers';
import {
  filterByDateRange,
  filterByStores,
  calculateKPIs,
  prepareRevenueChartData,
  prepareComparisonChartData,
  prepareYearComparisonChartData,
  prepareItemSalesChartData,
} from './FinancialDashboardController';
import { DashboardStore, PeriodTransaction, DailyTransaction, StoreSelectOption } from './types';
import './FinancialDashboard.scss';

// --- MOCK DATA TOGGLE ---
const USE_MOCK_DATA = false;
const PUBLIC_URL = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const MOCK_BASE_PATH = `${PUBLIC_URL}/temp/test`;
const MOCK_STORE_FILE = 'GetStoresForDashboard.json';

async function fetchMockFile<T = any>(fileName: string): Promise<T> {
  const response = await fetch(`${MOCK_BASE_PATH}/${fileName}`);
  if (!response.ok) {
    throw new Error(`Unable to load mock file ${fileName}`);
  }
  return response.json();
}
// --- MOCK DATA TOGGLE ---

function FinancialDashboard() {
  const { t } = useTranslation();
  const location = useLocation();
  const cashPaymentEnabled = !!useFeature('cashpayment');
  const { setShowLoadingModal, setToastMessage, setShowToastMessage, setToastType } = useUIState();

  // Check which sub-route we're on
  const isProductsRoute = location.pathname.includes('/products');
  const isOffersRoute = location.pathname.includes('/offers');

  const monthNames = [
    t('generic.months.january'), t('generic.months.february'), t('generic.months.march'),
    t('generic.months.april'), t('generic.months.may'), t('generic.months.june'),
    t('generic.months.july'), t('generic.months.august'), t('generic.months.september'),
    t('generic.months.october'), t('generic.months.november'), t('generic.months.december'),
  ];

  const { products } = useProductsAndOffers();

  // API data
  const [stores, setStores] = useState<DashboardStore[]>([]);
  const [transactions, setTransactions] = useState<PeriodTransaction[]>([]);
  const [dailyTransactions, setDailyTransactions] = useState<DailyTransaction[]>([]);

  // Filter state
  const [selectedStores, setSelectedStores] = useState<StoreSelectOption[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [activePeriod, setActivePeriod] = useState<'month' | 'quarter'>('month');

  useEffect(() => {
    localStorage.setItem('activeRoute', '/financial-dashboard');
    fetchDashboardData();
    // eslint-disable-next-line
  }, []);

  async function fetchDashboardData() {
    setShowLoadingModal(true);
    try {
      let storesList: DashboardStore[];

      if (USE_MOCK_DATA) {
        const storesPayload = await fetchMockFile(MOCK_STORE_FILE);
        storesList = storesPayload?.Stores || [];
      } else {
        const storesRes = await API.get('AE', '/Dashboard/GetStoresForDashboard', { response: true });
        storesList = storesRes.data.Stores;
      }
      setStores(storesList);

      // Load per-store transactions so each entry has a single store with its correct amount.
      const perStoreResults = await Promise.all(
        storesList.map(store =>
          USE_MOCK_DATA
            ? fetchMockFile(`GetTransactionsForDashboardStoreId${store.StoreId}.json`)
            : API.get('AE', `/Dashboard/GetTransactionsForDashboard`, {
                response: true,
                queryStringParameters: { StoreId: store.StoreId },
              }).then((res: any) => res.data)
        )
      );

      const allTransactions = perStoreResults.flatMap(
        (r: any) => r?.MonthlyTransactions || []
      );
      setTransactions(allTransactions);

      setDailyTransactions(
        perStoreResults.flatMap((r: any) => r?.DailyTransactions || [])
      );
    } catch (error) {
      console.log(error);
      setToastType('error');
      setToastMessage(t('financialdashboard.error.getting-transactions'));
      setShowToastMessage(true);
    } finally {
      setShowLoadingModal(false);
    }
  }

  // Store options for MultiSelect
  const storeOptions: StoreSelectOption[] = useMemo(
    () => stores.map(s => ({ value: s.StoreId, label: s.Code })),
    [stores]
  );

  // Store name lookup map
  const storeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    stores.forEach(s => map.set(s.StoreId, s.Code));
    return map;
  }, [stores]);

  // Selected store IDs
  const selectedStoreIds = useMemo(
    () => selectedStores.map(s => s.value),
    [selectedStores]
  );

  // Data filtered by store only (no date range) — used for "last month"/"last week" KPIs
  const storeFilteredData = useMemo(
    () => filterByStores(transactions, selectedStoreIds),
    [transactions, selectedStoreIds]
  );

  // Filtered data (store + date range)
  const filteredData = useMemo(
    () => filterByDateRange(storeFilteredData, fromDate, toDate),
    [storeFilteredData, fromDate, toDate]
  );

  // today's transaction count and amounts filtered by selected stores (no selection = all stores)
  const today = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const selectedCodes = selectedStores.map(s => s.value);
    return dailyTransactions
      .filter(d => d.Year === year && d.Month === month && d.Day === day)
      .filter(d => selectedCodes.length === 0 || d.Stores.some(code => selectedCodes.includes(code)))
      .reduce(
        (sum, d) => ({
          sales: sum.sales + d.TransactionCount,
          amountPaid: sum.amountPaid + d.TotalAmountPaid,
          amountCash: sum.amountCash + d.TotalAmountCash,
        }),
        { sales: 0, amountPaid: 0, amountCash: 0 }
      );
  }, [dailyTransactions, selectedStores]);

  // KPIs: filteredData for totals/averages, storeFilteredData for "last month"/"last week"
  const kpiData = useMemo(
    () => ({
      ...calculateKPIs(filteredData, storeFilteredData),
      todaySales: today.sales,
      todayAmountPaid: today.amountPaid,
      todayAmountCash: today.amountCash,
    }),
    // eslint-disable-next-line
    [filteredData, storeFilteredData, today]
  );

  // Chart data
  const revenueChartData = useMemo(
    () => prepareRevenueChartData(filteredData, monthNames, activePeriod),
    // eslint-disable-next-line
    [filteredData, activePeriod]
  );

  const comparisonChartData = useMemo(
    () => prepareComparisonChartData(filteredData, selectedStoreIds, storeNameMap, monthNames, activePeriod, t('financialdashboard.todays-sales-cash') as string, cashPaymentEnabled),
    // eslint-disable-next-line
    [filteredData, selectedStoreIds, storeNameMap, activePeriod, cashPaymentEnabled]
  );

  const yearComparisonChartData = useMemo(
    () => prepareYearComparisonChartData(filteredData, monthNames),
    // eslint-disable-next-line
    [filteredData]
  );

  const itemSalesChartData = useMemo(
    () => prepareItemSalesChartData(filteredData, monthNames, activePeriod, t('financialdashboard.items-sold') as string),
    // eslint-disable-next-line
    [filteredData, activePeriod]
  );

  return (
    <div className="financial-dashboard">
      <h1>{t('financialdashboard.title')}</h1>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <Link
          to="/financial-dashboard"
          className={`tab ${!isProductsRoute && !isOffersRoute ? 'active' : ''}`}
        >
          {t('financialdashboard.overview')}
        </Link>
        <Link
          to="/financial-dashboard/products"
          className={`tab ${isProductsRoute ? 'active' : ''}`}
        >
          {t('financialdashboard.products')}
        </Link>
        <Link
          to="/financial-dashboard/offers"
          className={`tab ${isOffersRoute ? 'active' : ''}`}
        >
          {t('financialdashboard.offers')}
        </Link>
      </div>

      {/* Render appropriate dashboard based on route */}
      {isProductsRoute ? (
        <ProductsDashboard
          transactions={transactions}
          stores={stores}
        />
      ) : isOffersRoute ? (
        <OffersDashboard
          monthlyTransactions={transactions}
        />
      ) : (
        <>
          {/* Filters bar */}
          <div className="filters-bar">
        <MultiSelect
          className='store-select'
          options={storeOptions}
          value={selectedStores}
          onChange={setSelectedStores}
          labelledBy={t('financialdashboard.select-stores')}
          overrideStrings={{
            selectSomeItems: t('financialdashboard.select-stores'),
            allItemsAreSelected: t('financialdashboard.all-stores'),
          }}
        />

        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <div className="filter-group">
            <label>{t('financialdashboard.from')}</label>
            <DatePicker
              className="datetimepicker-financial"
              clearable
              autoOk
              views={['year', 'month']}
              format="MM/yyyy"
              placeholder="MM/YYYY"
              onChange={(value) => setFromDate(value)}
              value={fromDate}
              animateYearScrolling={false}
            />
          </div>
          <div className="filter-group">
            <label>{t('financialdashboard.to')}</label>
            <DatePicker
              className="datetimepicker-financial"
              clearable
              autoOk
              views={['year', 'month']}
              format="MM/yyyy"
              placeholder="MM/YYYY"
              onChange={(value) => setToDate(value)}
              value={toDate}
              animateYearScrolling={false}
            />
          </div>
        </MuiPickersUtilsProvider>

        <div className="filter-group">
          <label>&nbsp;</label>
          <div className="period-buttons">
            <span
              className={`range-label ${activePeriod === 'month' ? 'active' : ''}`}
              onClick={() => setActivePeriod('month')}
            >
              {t('financialdashboard.per-month')}
            </span>
            <span
              className={`range-label ${activePeriod === 'quarter' ? 'active' : ''}`}
              onClick={() => setActivePeriod('quarter')}
            >
              {t('financialdashboard.per-quarter')}
            </span>
          </div>
        </div>

        <div className="filter-group">
          <label>&nbsp;</label>
          <ExportOverviewButton
            filteredData={filteredData}
            kpiData={kpiData}
            products={products}
            fromDate={fromDate}
            toDate={toDate}
            selectedStores={selectedStores}
            monthNames={monthNames}
            showCash={cashPaymentEnabled}
          />
        </div>
      </div>

      {/* KPI Cards */}
      <KPICards kpiData={kpiData} showCash={cashPaymentEnabled} />

          {/* Charts */}
          {filteredData.length > 0 ? (
            <div className="charts-row">
              <div className="chart-container">
                <RevenueChart chartData={revenueChartData} />
              </div>
              <div className="chart-container">
                <YearComparisonChart chartData={yearComparisonChartData} />
              </div>
            </div>
          ) : (
            <div className="no-data">
              {t('financialdashboard.select-stores')}
            </div>
          )}
          <div className="charts-row">
            <div className="chart-container">
              <ItemSalesChart chartData={itemSalesChartData} />
            </div>
            <div className="chart-container">
              <StoreComparisonChart chartData={comparisonChartData} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default FinancialDashboard;
